import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Lock, Unlock, Power } from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import Skeleton from '../../components/common/Skeleton';
import StatusBadge from '../../components/common/StatusBadge';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import EmptyState from '../../components/common/EmptyState';
import { adminService } from '../../services/adminService';
import { useToast } from '../../contexts/ToastContext';
import { formatBusinessType } from '../../utils/enums';

// Tela de detalhe da loja para REVITER (Seção 2.3.5/2.3.6 do doc).
// Sub-abas reservadas: Informações, Tema, Usuários, Produtos, Pedidos,
// Campanhas, Pagamentos. As fases seguintes plugam o conteúdo de cada
// sub-aba; aqui já preparamos a estrutura.
const TABS = [
  { id: 'info',       label: 'Informações' },
  { id: 'theme',      label: 'Tema' },
  { id: 'users',      label: 'Usuários' },
  { id: 'products',   label: 'Produtos' },
  { id: 'orders',     label: 'Pedidos' },
  { id: 'campaigns',  label: 'Campanhas' },
  { id: 'payments',   label: 'Pagamentos' },
];

const AdminStoreDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { error, success } = useToast();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('info');
  const [pendingAction, setPendingAction] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.getStore(id);
      setStore(data);
    } catch (e) {
      error(e.message || 'Erro ao carregar loja');
      navigate('/admin-reviter/lojas');
    } finally {
      setLoading(false);
    }
  }, [id, error, navigate]);

  useEffect(() => { load(); }, [load]);

  const runAction = async () => {
    if (!pendingAction) return;
    try {
      if (pendingAction === 'block') await adminService.blockStore(id);
      else if (pendingAction === 'unblock') await adminService.unblockStore(id);
      else if (pendingAction === 'deactivate') await adminService.deactivateStore(id);
      else if (pendingAction === 'activate') await adminService.activateStore(id);
      success('Loja atualizada');
      setPendingAction(null);
      load();
    } catch (e) {
      error(e.message);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto space-y-4">
        <Skeleton variant="text" width="40%" />
        <Skeleton variant="block" height={200} />
      </div>
    );
  }

  if (!store) return null;

  return (
    <div className="max-w-6xl mx-auto">
      <button
        type="button"
        onClick={() => navigate('/admin-reviter/lojas')}
        className="inline-flex items-center gap-2 text-sm text-app-text-secondary hover:text-app-text mb-4"
      >
        <ArrowLeft size={16} /> Voltar para lojas
      </button>

      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-3xl font-extrabold text-app-text mb-1">{store.name}</h1>
          <div className="flex flex-wrap gap-2 items-center text-sm text-app-text-secondary">
            <span>/{store.slug}</span>
            <Badge color={store.planType === 'CUSTOM' ? 'info' : 'neutral'} variant="soft" size="sm">
              {store.planType || 'DEFAULT'}
            </Badge>
            <Badge color="neutral" variant="soft" size="sm">{formatBusinessType(store.businessType) || '—'}</Badge>
            {store.deletedAt
              ? <Badge color="neutral" variant="soft" size="sm">Desativada</Badge>
              : store.isBlocked
                ? <Badge color="warning" variant="soft" size="sm">Bloqueada</Badge>
                : <StatusBadge domain="store" status={store.status} size="sm" />
            }
          </div>
        </div>
        <div className="flex gap-2">
          {store.deletedAt ? (
            <Button leftIcon={<Power size={16} />} onClick={() => setPendingAction('activate')}>
              Reativar
            </Button>
          ) : (
            <>
              {store.isBlocked ? (
                <Button leftIcon={<Unlock size={16} />} onClick={() => setPendingAction('unblock')}>
                  Desbloquear
                </Button>
              ) : (
                <Button variant="warning" leftIcon={<Lock size={16} />} onClick={() => setPendingAction('block')}>
                  Bloquear
                </Button>
              )}
              <Button variant="danger" leftIcon={<Power size={16} />} onClick={() => setPendingAction('deactivate')}>
                Desativar
              </Button>
            </>
          )}
        </div>
      </div>

      {store.isBlocked && !store.deletedAt && (
        <Card className="p-4 mb-4 bg-warning/5 border-warning/30">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-warning mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-app-text">Loja bloqueada</p>
              <p className="text-app-text-secondary">
                O whitelabel exibe tela de "serviço temporariamente indisponível" para os clientes.
                O MANAGER continua tendo acesso ao backoffice com banner de aviso.
              </p>
            </div>
          </div>
        </Card>
      )}

      <div className="border-b border-app-border mb-6 flex flex-wrap gap-1">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              tab === t.id
                ? 'border-primary text-primary'
                : 'border-transparent text-app-text-secondary hover:text-app-text'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'info' && <InfoTab store={store} />}
      {tab !== 'info' && (
        <Card className="p-8">
          <EmptyState
            title={`Sub-aba "${TABS.find((t) => t.id === tab)?.label}" em construção`}
            description="Esta seção será habilitada em uma fase posterior do roadmap."
          />
        </Card>
      )}

      <ConfirmDialog
        isOpen={!!pendingAction}
        onClose={() => setPendingAction(null)}
        onConfirm={runAction}
        title={
          pendingAction === 'block' ? 'Bloquear loja?' :
          pendingAction === 'unblock' ? 'Desbloquear loja?' :
          pendingAction === 'deactivate' ? 'Desativar loja?' :
          'Reativar loja?'
        }
        message={
          pendingAction === 'block'
            ? 'A loja continuará existindo mas o whitelabel exibirá tela de "indisponível".'
          : pendingAction === 'unblock'
            ? 'A loja voltará a ficar acessível pelo whitelabel.'
          : pendingAction === 'deactivate'
            ? 'A loja deixará de ser resolvida. Pode ser revertido a qualquer momento.'
          : 'A loja será restaurada e voltará a aparecer publicamente.'
        }
        confirmLabel={
          pendingAction === 'block' ? 'Bloquear' :
          pendingAction === 'unblock' ? 'Desbloquear' :
          pendingAction === 'deactivate' ? 'Desativar' :
          'Reativar'
        }
        variant={pendingAction === 'deactivate' || pendingAction === 'block' ? 'danger' : 'accent'}
      />
    </div>
  );
};

const InfoTab = ({ store }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
    <Card className="p-6">
      <h2 className="text-lg font-bold mb-4">Identificação</h2>
      <dl className="text-sm space-y-2">
        <Row label="Nome" value={store.name} />
        <Row label="Empresa" value={store.company || '—'} />
        <Row label="CNPJ" value={store.cnpj || '—'} />
        <Row label="Modelo" value={formatBusinessType(store.businessType) || '—'} />
        <Row label="Plano" value={store.planType || 'DEFAULT'} />
      </dl>
    </Card>

    <Card className="p-6">
      <h2 className="text-lg font-bold mb-4">MANAGER principal</h2>
      <dl className="text-sm space-y-2">
        <Row label="Nome" value={store.owner?.name || '—'} />
        <Row label="Email" value={store.owner?.email || '—'} />
        <Row label="Telefone" value={store.owner?.phone || '—'} />
      </dl>
    </Card>

    <Card className="p-6">
      <h2 className="text-lg font-bold mb-4">Domínio</h2>
      <p className="text-xs text-app-text-secondary mb-3">
        Domínio é imutável após o primeiro pedido — para alterar, contate o suporte.
      </p>
      <dl className="text-sm space-y-2">
        <Row label="Slug" value={`/${store.slug}`} mono />
        <Row label="Subdomínio" value={store.subdomain ? `${store.subdomain}.reviter.com` : '—'} mono />
        <Row
          label="Domínio próprio"
          value={
            <span>
              {store.customDomain || '—'}
              {store.customDomain && !store.customDomainVerified && (
                <Badge color="warning" size="sm" variant="soft" className="ml-2">Não verificado</Badge>
              )}
            </span>
          }
          mono
        />
      </dl>
    </Card>

    <Card className="p-6">
      <h2 className="text-lg font-bold mb-4">Operação</h2>
      <dl className="text-sm space-y-2">
        <Row label="Estoque baixo" value={`${store.lowStockThreshold ?? 5} unid.`} />
        <Row label="Gateway" value={(store.paymentGateway || 'asaas').toUpperCase()} />
        <Row label="Telefone suporte" value={store.supportPhone || '—'} />
        <Row label="WhatsApp" value={store.whatsappNumber || '—'} />
      </dl>
      <div className="mt-4">
        <Link to={`/admin-reviter/lojas/${store.id}/editar`}>
          <Button variant="subtle" size="sm">Editar dados</Button>
        </Link>
      </div>
    </Card>
  </div>
);

const Row = ({ label, value, mono }) => (
  <div className="flex justify-between gap-4 py-1 border-b border-app-border last:border-0">
    <dt className="text-app-text-secondary">{label}</dt>
    <dd className={mono ? 'font-mono text-app-text' : 'text-app-text'}>{value}</dd>
  </div>
);

export default AdminStoreDetail;
